import { useEffect, useMemo, useState } from "preact/hooks";
import type { FormDefinition } from "@fobu/schema";
import { localize, pickLanguage, renderTemplate } from "@fobu/engine";
import type { FormClient, ResumeState } from "../lib/client";
import { FormContext, type FormEnv } from "../lib/context";
import { useFormController } from "../lib/controller";
import { loadLocale, RTL, setLocale, t } from "../lib/i18n";
import { themeToCssVars, themeAttrs, customFontCss } from "../lib/theme";
import { wasSubmitted } from "../lib/draft";
import { ClassicView } from "./ClassicView";
import { EndingView, MessageView } from "./EndingView";
import { LazyConversational, LazyLiveParticipant } from "./Common";

export interface FormAppProps {
  def: FormDefinition;
  versionId: string;
  client: FormClient;
  draftKey: string | null;
  initialAnswers?: Record<string, unknown>;
  /** Feld, dessen Antwort aus der Einladungsmail stammt – Start bei der ersten offenen Frage. */
  fromMail?: string;
  hidden?: Record<string, string>;
  remaining?: Record<string, number>;
  closed?: boolean;
  /** Für Tests und Vorschau: aktuelle Zeit überschreiben. */
  now?: Date;
  /** Schlüssel für „nur einmal pro Gerät“ (Slug); ohne Angabe kein weicher Duplikatschutz. */
  onceKey?: string;
  initialOther?: Record<string, string>;
  /** Zwischenstand aus einem Fortsetzungslink. */
  resume?: ResumeState & { token: string };
  /** Statt der Endseite: Rückmeldung an den Aufrufer (Selbstbearbeitung). */
  onSubmitted?: () => void;
}

function availability(def: FormDefinition, now: Date, closed?: boolean): "open" | "closed" | "not_yet" {
  if (closed) return "closed";
  if (def.settings.opensAt && now < new Date(def.settings.opensAt)) return "not_yet";
  if (def.settings.closesAt && now >= new Date(def.settings.closesAt)) return "closed";
  return "open";
}

function FormInner(props: FormAppProps) {
  const ctl = useFormController({
    def: props.def,
    versionId: props.versionId,
    client: props.client,
    draftKey: props.draftKey,
    ...(props.initialAnswers ? { initialAnswers: props.initialAnswers } : {}),
    ...(props.hidden ? { hidden: props.hidden } : {}),
    ...(props.onceKey ? { onceKey: props.onceKey } : {}),
    ...(props.initialOther ? { initialOther: props.initialOther } : {}),
    ...(props.resume ? { resume: props.resume } : {}),
    ...(props.fromMail ? { fromMail: props.fromMail } : {}),
  });
  const done = ctl.status === "done";
  useEffect(() => {
    if (done) props.onSubmitted?.();
  }, [done]);
  const env: FormEnv = useMemo(
    () => ({
      ctl,
      client: props.client,
      versionId: props.versionId,
      remaining: props.remaining ?? {},
      tpl: (text: string) => renderTemplate(text, ctl.idx, { answers: ctl.flow.answers, other: ctl.other, vars: ctl.flow.variables, hidden: ctl.flow.hidden }),
    }),
    [ctl, props.client, props.versionId, props.remaining],
  );
  return (
    <FormContext.Provider value={env}>
      {ctl.status === "done" && props.onSubmitted ? null : ctl.status === "done" && ctl.ending ? <EndingView /> : ctl.conversational ? <LazyConversational /> : <ClassicView />}
    </FormContext.Provider>
  );
}

const LANG_NAMES: Record<string, string> = { de: "Deutsch", en: "English", fr: "Français", it: "Italiano", es: "Español", tr: "Türkçe", ar: "العربية", uk: "Українська", ru: "Русский", pl: "Polski", el: "Ελληνικά", hr: "Hrvatski", fa: "فارسی" };

function LanguageSwitch({ langs, value, onChange }: { langs: string[]; value: string; onChange: (l: string) => void }) {
  return (
    <div class="fb-lang">
      <label class="fb-sr" for="fb-lang-select">
        {t.language}
      </label>
      <select id="fb-lang-select" class="fb-lang-select" value={value} onChange={(e) => onChange(e.currentTarget.value)}>
        {langs.map((l) => (
          <option key={l} value={l} lang={l}>
            {LANG_NAMES[l] ?? l.toUpperCase()}
          </option>
        ))}
      </select>
    </div>
  );
}

export function FormApp(props: FormAppProps) {
  const base = props.def;
  const [lang, setLang] = useState(() =>
    pickLanguage(base, typeof location !== "undefined" ? new URLSearchParams(location.search).get("lang") : null, typeof navigator !== "undefined" ? navigator.languages : []),
  );
  // Sprache gilt für Formulartexte (localize) und Oberfläche (setLocale). Antworten bleiben beim Umschalten erhalten.
  const def = useMemo(() => localize(base, lang), [base, lang]);
  const ready = setLocale(lang);
  const [, rerender] = useState(0);
  useEffect(() => {
    if (!ready) void loadLocale(lang).then(() => rerender((x) => x + 1));
  }, [lang, ready]);
  const langs = [base.settings.language, ...(base.settings.languages ?? [])];
  const vars = useMemo(() => themeToCssVars(def.theme), [def.theme]);
  const attrs = useMemo(() => themeAttrs(def.theme), [def.theme]);
  const fontCss = customFontCss(def.theme);
  const state = availability(def, props.now ?? new Date(), props.closed);
  // Beim Rendern einmal prüfen – nach dem eigenen Absenden soll die Endseite stehen bleiben.
  const [already] = useState(() => !!props.onceKey && def.settings.limitOnePer === "browser" && wasSubmitted(props.onceKey));

  useEffect(() => {
    document.title = def.title;
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL.has(lang.split("-")[0]!) ? "rtl" : "ltr";
  }, [def.title, lang]);

  // Englische Oberflächentexte kommen gleich – nicht kurz Deutsch zeigen.
  if (!ready) return <div class={`fb-root fb-mode-${def.settings.mode}`} style={vars} {...attrs} aria-busy="true" />;
  return (
    <div class={`fb-root fb-mode-${def.settings.mode}`} style={vars} {...attrs}>
      {fontCss && <style>{fontCss}</style>}
      {def.theme.customCss && <style>{def.theme.customCss}</style>}
      {langs.length > 1 && <LanguageSwitch langs={langs} value={lang} onChange={setLang} />}
      {already ? (
        <MessageView title={t.alreadySubmitted} body={t.alreadySubmittedBody} />
      ) : state === "open" ? (
        base.settings.live?.enabled && props.client.live ? (
          <LazyLiveParticipant def={def} client={props.client} versionId={props.versionId} />
        ) : (
          <FormInner {...props} def={def} />
        )
      ) : (
        <MessageView title={state === "closed" ? t.closedTitle : t.notOpenYet} body={def.settings.closedMessage} />
      )}
    </div>
  );
}
